import React, { Component } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import cx from 'classnames'
import _ from 'lodash'

import { FilterTable } from 'gnos-tables'

import CustomDate from '../../../../../../../../../../../components/CustomDate'
import Currency from '../../../../../../../../../../../components/Currency'
import Money from '../../../../../../../../../../../components/TableCell/components/Money'
import Entity from '../../../../../../../../../../../components/TableCell/components/Entity'
import NoData from '../../../../../../../../../components/Workspace/components/NoData'
import Link from '../../../../../../../../../../../components/TableCell/components/Link'
import FailedLoad from '../../../../../../../../../components/Workspace/components/FailedLoad'

import { LOCALE_PREFIX } from '../../constants'

import hocConnect from './hocConnect'
import './style.scss'

@injectIntl
@hocConnect
export default class PaymentOrders extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        customData: PropTypes.object,
        data: PropTypes.array,
        dataLocale: PropTypes.string,
        failed: PropTypes.bool,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        isLoading: PropTypes.bool,
        level: PropTypes.string.isRequired,
        loaded: PropTypes.bool,
        locale: PropTypes.string.isRequired,
        locked: PropTypes.bool,
        module: PropTypes.string.isRequired,
        negativeEventsLoadTableData: PropTypes.func.isRequired,
        negativeEventsTableChange: PropTypes.func.isRequired,
        pagination: PropTypes.object,
        sorted: PropTypes.array,
        tableId: PropTypes.string.isRequired,
        tableSelector: PropTypes.string.isRequired,
    }

    componentDidMount() {
        const { loaded, dataLocale, locale } = this.props

        if (!loaded || dataLocale !== locale) this.loadData()
    }

    shouldComponentUpdate(nextProps) {
        const {
            customData,
            data,
            isLoading,
            level,
            locale,
            pagination,
            sorted,
            loaded,
            failed,
            tableId,
            tableSelector,
        } = this.props

        if (!_.isEqual(customData, nextProps.customData)) return true
        if (!_.isEqual(data, nextProps.data)) return true
        if (!_.isEqual(isLoading, nextProps.isLoading)) return true
        if (!_.isEqual(level, nextProps.level)) return true
        if (!_.isEqual(locale, nextProps.locale)) return true
        if (!_.isEqual(pagination, nextProps.pagination)) return true
        if (!_.isEqual(sorted, nextProps.sorted)) return true
        if (!_.isEqual(loaded, nextProps.loaded)) return true
        if (!_.isEqual(failed, nextProps.failed)) return true
        if (!_.isEqual(tableId, nextProps.tableId)) return true
        if (!_.isEqual(tableSelector, nextProps.tableSelector)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { level, locale, dataLocale } = this.props

        if (prevProps.level !== level) this.loadData()
        if (locale !== dataLocale && dataLocale) this.loadData()
    }

    loadData = () => {
        const { id, module, block, level, negativeEventsLoadTableData, tableId, tableSelector, customData } = this.props

        negativeEventsLoadTableData(
            tableId,
            tableSelector,
            id, // Entity ID
            module,
            block,
            level,
            customData
        )
    }

    handleTableChange = (changeData) => {
        const { negativeEventsTableChange, tableId, tableSelector, id, module, block, level, customData } = this.props

        negativeEventsTableChange(tableId, tableSelector, id, module, block, level, customData, changeData)
    }

    render() {
        const { intl, tableId, data, pagination, sorted, isLoading, failed, locked } = this.props

        if (locked && !isLoading && !failed) {
            return (
                <div className={'ne-payment-orders'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.lockedTab` })} />
                </div>
            )
        }

        // Failed data load
        if (failed && !isLoading) {
            return (
                <div className={'ne-payment-orders'}>
                    <FailedLoad
                        customCallback={this.loadData}
                        customText={intl.formatMessage({ id: 'global.failedToLoadTabContent' })}
                        show={failed}
                    />
                </div>
            )
        }

        // Empty data
        if (_.isEmpty(data) && !isLoading) {
            return (
                <div className={'ne-payment-orders'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.noData` })} />
                </div>
            )
        }

        return (
            <div className={'ne-payment-orders'}>
                <div className={'table-container'}>
                    <FilterTable
                        id={tableId}
                        data={data ?? []}
                        loading={isLoading}
                        columnsDefinition={[
                            {
                                accessor: 'folderId',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.fileNumber` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        {_.get(value, 'value', '')}
                                    </div>
                                ),
                            },
                            {
                                accessor: 'entitled',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.eligibleSubject` }),
                                Cell: (value) => {
                                    if (!_.isNil(_.get(value, 'value.type'))) {
                                        return (
                                            <div className={'centered-cell'}>
                                                <Entity
                                                    entity={_.get(value, 'value', null)}
                                                    type={_.get(value, 'value.type', null)}
                                                    inactive={_.get(value, 'value.inactive', false)}
                                                    rowInactive={_.get(value, 'original.inactive', false)}
                                                    value={_.get(value, 'value.name', null)}
                                                    entId={_.get(value, 'value.id', null)}
                                                    risk={_.get(value, 'value.indicators.risk', null)}
                                                    caution={_.get(value, 'value.indicators.caution', null)}
                                                    notice={_.get(value, 'value.indicators.notice', null)}
                                                />
                                            </div>
                                        )
                                    }

                                    return (
                                        <div
                                            className={cx(
                                                'centered-cell',
                                                _.get(value, 'original.inactive', false) && 'inactive'
                                            )}
                                        >
                                            <FormattedMessage id={`${LOCALE_PREFIX}.anonymousPerson`} />
                                        </div>
                                    )
                                },
                            },
                            {
                                accessor: 'decisionDate',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.date` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <CustomDate
                                            date={_.get(value, 'value.value', null)}
                                            precision={_.get(value, 'value.precision', null)}
                                        />
                                    </div>
                                ),
                                sortable: true,
                                width: 125,
                            },
                            {
                                accessor: 'amount',
                                sortable: true,
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.amount` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <Money value={_.get(value, 'value', {})} showCurrency={false} />
                                    </div>
                                ),
                            },
                            {
                                accessor: 'amount.currency',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.currency` }),
                                width: 75,
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <Currency currency={value?.value} />
                                    </div>
                                ),
                            },
                            {
                                accessor: 'link',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.direction` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <Link
                                            label={_.get(value, 'value.label', '')}
                                            url={_.get(value, 'value.url', '')}
                                            showIcon
                                            iconType={'pdf'}
                                            openOnNewTab
                                        />
                                    </div>
                                ),
                            },
                        ]}
                        settings={{
                            pagination,
                            sorted,
                        }}
                        onTableChange={this.handleTableChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        texts={{
                            pagination: {
                                page: intl.formatMessage({ id: 'global.page' }),
                                from: intl.formatMessage({ id: 'global.of' }),
                                rowCount: intl.formatMessage({ id: 'global.rowCount' }),
                                rowShow: intl.formatMessage({ id: 'global.rowShow' }),
                                all: intl.formatMessage({ id: 'global.all' }),
                            },
                        }}
                    />
                </div>
            </div>
        )
    }
}
